import { z } from "zod";

/**
 * Le contrat d'authentification, partage par l'API et le front. Il vit ici pour
 * la meme raison que le document : une forme decrite a deux endroits finit par
 * diverger, et c'est l'ecran de connexion qui le paie.
 */

/** Ce que l'API rend d'un utilisateur connecte. Jamais l'empreinte, jamais le sel. */
export const sessionUserSchema = z.object({
  id: z.string().min(1),
  login: z.string().min(1),
  role: z.enum(["USER", "ADMIN"]),
});

export type SessionUser = z.infer<typeof sessionUserSchema>;

/**
 * Les bornes sont larges a dessein. Ce schema n'est pas une politique de mot de
 * passe : la route de connexion ne doit jamais renvoyer autre chose que son 401
 * unique, et un refus de forme serait deja une reponse.
 */
export const loginRequestSchema = z.object({
  login: z.string().min(1).max(64),
  motDePasse: z.string().min(1).max(512),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

/** Le login est normalise avant tout : « Alice » et « alice » sont le meme compte. */
export function normaliserLogin(brut: unknown): unknown {
  return typeof brut === "string" ? brut.trim().toLowerCase() : brut;
}

/**
 * La politique de mot de passe, appliquee a la CREATION et au changement — pas
 * a la connexion. Un compte existant dont le mot de passe ne la respecte plus
 * doit pouvoir se connecter : c'est a ce moment-la qu'on peut lui demander d'en
 * changer, pas en le laissant dehors.
 */
export const MOT_DE_PASSE_MINIMUM = 12;

export function motDePasseAcceptable(motDePasse: string): string | undefined {
  if (motDePasse.length < MOT_DE_PASSE_MINIMUM) {
    return `Le mot de passe doit faire au moins ${MOT_DE_PASSE_MINIMUM} caracteres.`;
  }
  if (motDePasse.length > 512) return "Le mot de passe est trop long.";
  // Un mot de passe entierement numerique de 12 chiffres se casse hors ligne en
  // quelques heures quel que soit l'algorithme : 10^12 possibilites, c'est
  // moins qu'un mot de passe de 7 caracteres melanges.
  if (/^\d+$/.test(motDePasse)) {
    return "Un mot de passe uniquement numerique se casse trop vite. Melangez lettres et chiffres, ou prenez une phrase.";
  }
  return undefined;
}
